import React, { useCallback } from 'react';

import { WatchQueryFetchPolicy } from '@apollo/client';
import { CustomEventNames, EventTypes, logEvent } from 'features/analytics';
import { useStoreContext } from 'features/store';

import { IHistoricalOrder } from '@rbi-ctg/menu';
import {
  IGetUserOrdersQuery,
  IGetUserOrdersQueryVariables,
  RbiOrderStatus,
  useGetUserOrdersQuery,
} from 'generated/graphql-gateway';
import { useAuthContext } from 'state/auth';
import { useServiceModeLive } from 'state/service-mode';
import logger from 'utils/logger';

import { useCartHydration } from './use-cart-hydration.ts';

export const UserOrdersDefaultLimit = 5;

export const UserOrdersDefaultVariables = {
  orderStatuses: [
    RbiOrderStatus.INSERT_SUCCESSFUL,
    RbiOrderStatus.UPDATE_SUCCESSFUL,
    RbiOrderStatus.REFUND_SUCCESSFUL,
    RbiOrderStatus.REFUND_ERROR,
    RbiOrderStatus.REFUND_REQUESTED,
  ],
  shouldFilterByRegion: true,
};

export const useUserOrders = (
  variables: IGetUserOrdersQueryVariables,
  fetchPolicy: WatchQueryFetchPolicy = 'cache-and-network'
) => {
  const { store } = useStoreContext();
  const { prepareReorderCartEntries } = useCartHydration({
    minimalCartEntries: [],
    cartEntries: [],
  });

  const [ordersData, setOrdersData] = React.useState<IGetUserOrdersQuery>();
  const [fetchingMore, setFetchingMore] = React.useState<boolean>(false);
  const [isHydratingOrders, setIsHydratingOrders] = React.useState<boolean>(false);

  // Raw (unhydrated) orders currently known, kept in a ref so appending a "load more" page never
  // triggers the target-change rehydration effect below - it's read fresh whenever that effect
  // actually needs to rehydrate everything for a new store/service mode.
  const rawOrdersRef = React.useRef<IHistoricalOrder[]>([]);
  const hydratedOrdersTargetRef = React.useRef<string | undefined>(undefined);

  const { isAuthenticated } = useAuthContext();
  const serviceMode = useServiceModeLive();

  const hydrationTarget = [
    store.number ?? '',
    store.pos?.vendor?.toLocaleLowerCase() ?? '',
    store.isOneMenu ? 'one-menu' : 'legacy',
    serviceMode ?? '',
  ].join(':');

  const checkIfOrdersNeedHydration = useCallback(
    () => rawOrdersRef.current.length > 0 && hydratedOrdersTargetRef.current !== hydrationTarget,
    [hydrationTarget]
  );

  const hydrateOrders = useCallback(
    async (orders: IHistoricalOrder[]) => {
      // Skip rehydration when no store is selected – the newStore would have
      // an empty storeId, which causes the RehydrateCart mutation to fail.
      if (!store.number) {
        return orders;
      }

      const result: IHistoricalOrder[] = [];

      for (const order of orders) {
        try {
          const hydratedBackendCartEntries = await prepareReorderCartEntries({
            order,
            newStore: store,
          });

          result.push(({
            ...order,
            cart: {
              ...order.cart,
              cartEntries: hydratedBackendCartEntries,
            },
          } as unknown) as IHistoricalOrder);
        } catch {
          // fallback
          result.push((order as unknown) as IHistoricalOrder);
        }
      }
      return result;
    },
    [prepareReorderCartEntries, store]
  );

  // Hydrates the full raw order set for the current target and replaces ordersData outright.
  // Used for the initial load and refetches, where the whole order set is new/different.
  const hydrateAndReplaceOrders = useCallback(
    async (data: IGetUserOrdersQuery) => {
      const rawOrders = ((data.userOrders.orders || []) as unknown) as IHistoricalOrder[];
      rawOrdersRef.current = rawOrders;

      const targetAtStart = hydrationTarget;

      setIsHydratingOrders(true);

      try {
        const hydratedOrders = await hydrateOrders(rawOrders);

        setOrdersData({
          ...data,
          userOrders: {
            ...data.userOrders,
            orders: hydratedOrders as any[],
          },
        });
        hydratedOrdersTargetRef.current = targetAtStart;
      } finally {
        setIsHydratingOrders(false);
      }
    },
    [hydrateOrders, hydrationTarget]
  );

  const { error, loading, refetch, fetchMore } = useGetUserOrdersQuery({
    variables: {
      ...UserOrdersDefaultVariables,
      ...variables,
    },
    onCompleted: data => {
      hydrateAndReplaceOrders(data);
    },
    context: { shouldNotBatch: true },
    skip: !isAuthenticated,
    fetchPolicy,
  });

  // Re-hydrate the known orders whenever the store/service-mode target changes - previously
  // hydrated cart entries can reference the old store's vendor/menu data. This does NOT run for
  // "load more" pages (handleLoadMore hydrates and appends those itself) - only for a genuine
  // target change, so the on-screen list is never swapped for a full loading skeleton while
  // simply appending more orders.
  React.useEffect(() => {
    if (!checkIfOrdersNeedHydration()) {
      return;
    }

    let isCurrentHydration = true;
    setIsHydratingOrders(true);

    const hydrateRawOrders = async () => {
      try {
        const hydratedOrders = await hydrateOrders(rawOrdersRef.current);

        if (!isCurrentHydration) {
          return;
        }

        setOrdersData(prevOrdersData =>
          prevOrdersData
            ? {
                ...prevOrdersData,
                userOrders: {
                  ...prevOrdersData.userOrders,
                  orders: hydratedOrders as any[],
                },
              }
            : prevOrdersData
        );

        hydratedOrdersTargetRef.current = hydrationTarget;
      } finally {
        if (isCurrentHydration) {
          setIsHydratingOrders(false);
        }
      }
    };

    hydrateRawOrders();

    return () => {
      // A store or service-mode change can start another hydration before this one resolves.
      // Ignore the older result so it cannot overwrite entries prepared for the latest target.
      isCurrentHydration = false;
    };
  }, [checkIfOrdersNeedHydration, hydrateOrders, hydrationTarget]);

  const resumeAfter = ordersData?.userOrders.resumeAfter;
  const count = ordersData?.userOrders.count || 0;
  const orders = ((ordersData?.userOrders.orders || []) as unknown) as IHistoricalOrder[];

  const handleLoadMore = useCallback(async () => {
    if (!resumeAfter) {
      return;
    }

    logEvent(CustomEventNames.LOAD_MORE, EventTypes.Other, {
      'Source Page': '/recentitems',
    });

    setFetchingMore(true);

    try {
      const { data: fetchMoreResult } = await fetchMore({
        variables: { resumeAfter },
      });

      const newRawOrders = ((fetchMoreResult.userOrders.orders ||
        []) as unknown) as IHistoricalOrder[];

      // Keep the raw-orders ref up to date so a later store/service-mode change rehydrates this
      // page too.
      rawOrdersRef.current = [...rawOrdersRef.current, ...newRawOrders];

      // Only hydrate the newly fetched page - the orders already on screen are hydrated already.
      // isHydratingOrders is intentionally left untouched here, so the existing list stays
      // mounted and visible (no full-list reload/skeleton) while just the new page loads.
      const hydratedNewOrders = await hydrateOrders(newRawOrders);

      setOrdersData(prevOrdersData => ({
        ...fetchMoreResult,
        userOrders: {
          ...fetchMoreResult.userOrders,
          orders: [...(prevOrdersData?.userOrders.orders || []), ...hydratedNewOrders] as any[],
        },
      }));

      return fetchMoreResult;
    } catch (error) {
      logger.warn({
        message: '[useUserOrders] Failed to fetch more recent orders',
        error,
      });
    } finally {
      setFetchingMore(false);
    }
  }, [fetchMore, hydrateOrders, resumeAfter]);

  // Mark the data stale during the render where the hydration target changes. Waiting for the
  // effect to set loading would briefly render entries hydrated for the previous store.
  const ordersNeedHydration = checkIfOrdersNeedHydration();

  const isHydratingOrder = isHydratingOrders || ordersNeedHydration;

  return {
    error,
    loading,
    orders,
    count,
    resumeAfter,
    refetch,
    fetchMore,
    handleLoadMore,
    fetchingMore,
    isHydratingOrder,
  };
};
